/**
 * Cookie / tracking consent store (29 JUN 2026).
 *
 * Single source of truth for the visitor's cookie choice, shared by the cookie
 * banner (components/CookieConsent.tsx) and the analytics gate (lib/analytics).
 *
 * Storage key is intentionally the SAME one the static landing page writes
 * (`qastudio.v1.cookieConsent`), so a choice made on the landing carries into
 * the app when served from the same origin — and vice versa.
 *
 * GDPR posture (operator established in the EU): optional categories are OFF
 * until the visitor opts in. «necessary» is always on. Today the only optional
 * category is «analytics»; «functional» storage (progress, theme) is treated as
 * strictly necessary for the SPA to work and needs no separate toggle.
 */

const KEY = 'qastudio.v1.cookieConsent';
const EVENT = 'qastudio:consent-changed';
const SCHEMA_VERSION = 1;

export interface ConsentState {
  v: number;
  necessary: true;
  analytics: boolean;
  /** ISO timestamp of the decision. */
  ts: string;
}

function isConsentState(value: unknown): value is ConsentState {
  if (typeof value !== 'object' || value === null) return false;
  const c = value as Record<string, unknown>;
  return typeof c.analytics === 'boolean' && typeof c.ts === 'string';
}

/** Read the stored decision, or `null` if the visitor has not chosen yet. */
export function readConsent(): ConsentState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!isConsentState(parsed)) return null;
    return { v: SCHEMA_VERSION, necessary: true, analytics: parsed.analytics, ts: parsed.ts };
  } catch {
    return null;
  }
}

/** Has the visitor made any cookie choice yet? Drives whether the banner shows. */
export function hasDecided(): boolean {
  return readConsent() !== null;
}

/** Convenience: is product analytics permitted right now? */
export function analyticsAllowed(): boolean {
  return readConsent()?.analytics === true;
}

/** Persist a decision and notify listeners (banner, analytics, other tabs). */
export function saveConsent(opts: { analytics: boolean }): ConsentState {
  const state: ConsentState = {
    v: SCHEMA_VERSION,
    necessary: true,
    analytics: opts.analytics,
    ts: new Date().toISOString(),
  };
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(state));
    } catch {
      /* private mode / quota — choice just won't persist */
    }
    try {
      window.dispatchEvent(new CustomEvent<ConsentState>(EVENT, { detail: state }));
    } catch {
      /* CustomEvent unsupported — listeners simply won't fire */
    }
  }
  return state;
}

/** Accept-all shortcut (analytics on). */
export function acceptAll(): ConsentState {
  return saveConsent({ analytics: true });
}

/** Reject-optional shortcut (necessary only). */
export function rejectOptional(): ConsentState {
  return saveConsent({ analytics: false });
}

/**
 * Subscribe to consent changes (same tab via CustomEvent, other tabs via the
 * native `storage` event). Returns an unsubscribe function.
 */
export function subscribeConsent(cb: (state: ConsentState | null) => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const onCustom = () => cb(readConsent());
  const onStorage = (e: StorageEvent) => {
    if (e.key === KEY) cb(readConsent());
  };
  window.addEventListener(EVENT, onCustom as EventListener);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(EVENT, onCustom as EventListener);
    window.removeEventListener('storage', onStorage);
  };
}
